import { Head } from "@inertiajs/react";
import { useEffect, useRef, type ReactNode } from "react";
import AppShell from "@/components/AppShell";
import OnlineLink from "@/components/OnlineLink";
import PageHeader from "@/components/PageHeader";
import { useScrollOverflow } from "@/hooks/useScrollOverflow";
import { NEEDS_NETWORK, useOnline } from "@/hooks/useOnline";

const TABS = [
  { key: "devices", label: "Устройства", href: "/settings/devices" },
  { key: "telegram", label: "Telegram", href: "/settings/telegram_link" },
  { key: "ai_actions", label: "AI-действия", href: "/settings/ai_actions" },
  { key: "later_keywords", label: "Сразу в «Задачи»", href: "/settings/later_keywords" },
  { key: "appearance", label: "Вид", href: "/settings/appearance" },
  { key: "mcp_tokens", label: "API / MCP", href: "/settings/mcp_tokens" },
  { key: "trash", label: "Недавно удалённые", href: "/settings/trash" },
] as const;

const SCROLL_STEP = 0.6;

type TabKey = (typeof TABS)[number]["key"];

interface Props {
  active: TabKey;
  title: string;
  children: ReactNode;
}

export default function SettingsLayout({ active, title, children }: Props) {
  const activeTabRef = useRef<HTMLAnchorElement>(null);
  const { ref: tabStripRef, overflow } = useScrollOverflow<HTMLElement>();
  const online = useOnline();

  const scrollTabs = (direction: 1 | -1) => {
    const strip = tabStripRef.current;
    strip?.scrollBy?.({ left: direction * strip.clientWidth * SCROLL_STEP, behavior: "smooth" });
  };

  useEffect(() => {
    activeTabRef.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [active]);

  return (
    <AppShell>
      <Head title={title} />
      <div className="max-w-2xl mx-auto pb-8">
        <PageHeader title="Настройки" />
        <div className="relative">
          <nav
            ref={tabStripRef}
            className="flex gap-1.5 overflow-x-auto no-scrollbar px-5 md:px-7 pb-1"
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <OnlineLink
                  key={tab.key}
                  ref={isActive ? activeTabRef : undefined}
                  href={tab.href}
                  aria-current={isActive ? "page" : undefined}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-blue-500 text-white font-medium"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                  }`}
                >
                  {tab.label}
                </OnlineLink>
              );
            })}
          </nav>
          {(overflow === "start" || overflow === "both") && (
            <ScrollArrow side="start" onClick={() => scrollTabs(-1)} />
          )}
          {(overflow === "end" || overflow === "both") && (
            <ScrollArrow side="end" onClick={() => scrollTabs(1)} />
          )}
        </div>
        <fieldset
          disabled={!online}
          title={online ? undefined : NEEDS_NETWORK}
          className="min-w-0 px-5 md:px-7 pt-5 space-y-5"
        >
          {children}
        </fieldset>
      </div>
    </AppShell>
  );
}

function ScrollArrow({ side, onClick }: { side: "start" | "end"; onClick: () => void }) {
  const isEnd = side === "end";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isEnd ? "Ещё вкладки" : "Предыдущие вкладки"}
      className={`absolute top-0 bottom-1 flex items-center px-2 text-2xl leading-none text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white from-gray-50 from-60% dark:from-gray-900 ${
        isEnd ? "right-0 pl-5 bg-gradient-to-l" : "left-0 pr-5 bg-gradient-to-r"
      }`}
    >
      {isEnd ? "›" : "‹"}
    </button>
  );
}
